import React from 'react';
import Button from 'react-md/lib/Buttons';
import SelectField from 'react-md/lib/SelectFields/SelectField';
import LinearProgress from 'react-md/lib/Progress/LinearProgress';
import { CSSTransition } from 'react-transition-group';

import { withT } from 'lib/i18n';
import { ComponentProps } from './types';
import { SearchPeriod } from 'types/search/form';
import SearchDateRange from 'components/Search/DateRange';
import { Tenant } from "class/Tenant";

import './FocusFeedForm.scss';
import featureFlags from 'lib/featureFlags';

type ComponentState = {
	dateRangeAnimating: boolean
};

class FocusFeedForm extends React.Component<ComponentProps, ComponentState> {

	constructor(props: ComponentProps) {
		super(props);
		this.state = { dateRangeAnimating: false };
	}

	private _getSortSelectField = () => {
		const { t, feedType, sort, tenant: tenantObject } = this.props;
		const { onSetSort } = this.props;

		const audienceSort = new Tenant(tenantObject).getAudienceFieldSort() + ':desc';

		let menuItems;
		if (feedType === 'print') {
			menuItems = [
				{ label: t('results.sort.audience'), value: 'photo.section.issue.circulation:desc', key: 'photo.section.issue.circulation:desc' },
				{ label: t('results.sort.publication_date'), value: 'publication_date:desc', key: 'photo.section.issue.date:desc' },
				{ label: t('results.sort.echo'), value: 'echo:desc', key: 'echo:desc' },
				{ label: t('insert_article.modal.form.miv'), value: 'valorization:desc', key: 'valorization:desc' }
			];
		}
		else if (feedType === 'print_dmr') {
			menuItems = [
				{ label: t('results.sort.audience'), value: 'print.source.circulation:desc', key: 'print.source.circulation:desc' },
				{ label: t('results.sort.publication_date'), value: 'publication_date:desc', key: 'published_at:desc' },
				{ label: t('insert_article.modal.form.miv'), value: 'miv.value:desc', key: 'miv.value:desc'}
			];
		}
		else {
			menuItems = [
				{ label: t('results.sort.audience'), value: audienceSort, key: audienceSort },
				{ label: t('results.sort.publication_date'), value: 'publication_date:desc', key: 'publication_date:desc' },
				{ label: t('results.sort.echo'), value: 'echo:desc', key: 'echo:desc' },
				{ label: t('insert_article.modal.form.miv'), value: 'miv:desc', key: 'miv:desc'}
			];
		}

		if(featureFlags.isEnabled('no-miv')) {
			menuItems = menuItems.filter(item => item.label !== t('insert_article.modal.form.miv'));
		}

		return <SelectField
			id="focusFeedSortBySelectField"
			label={t('results.sort.value')}
			repositionOnResize
			menuItems={menuItems}
			onChange={value => onSetSort(value.toString())}
			defaultValue={sort}
			value={sort}
		/>;
	}

	private _getCustomPeriodSelectors = () => {
		const { dateRangeAnimating } = this.state;
		const { searchPeriod, feedType } = this.props;

		const isCustomPeriod = searchPeriod === "custom";
		const content = isCustomPeriod || dateRangeAnimating ? <SearchDateRange previewType={feedType!} /> : <></>;

		return (
			<div id="focusFeedDateSelectors">
				<CSSTransition
					id="focusFeedDateSelectorsTransition"
					classNames="focus-feed-form-show-date-selectors"
					timeout={500}
					in={!!isCustomPeriod}
					onExited={() => this.setState({ dateRangeAnimating: false })}>
					{content}
				</CSSTransition>
			</div>
		);
	}

	private _getDisplayingCounterContent = () => {
		const { t, total, loadingDocuments, start, limit } = this.props;

		if (loadingDocuments || !total) return null;
		return (
			<div className="form-display-items">
				<div className="form-results-display-label">{t('results.search.documents.pagination_label')}</div>
				<div className="form-results-display-content">{t('results.search.documents.pagination_info', {
					start: start + 1,
					end: total < limit ? total : start + limit,
					count: total > 5000 ? 5000 : total
				})}</div>
			</div>
		);
	}

	private _getPeriodSelectorContent = () => {
		const { t, searchPeriod } = this.props;
		const { onSetPeriod } = this.props;

		const periodSelectItems = [
			{ label: t("filters.period.last_week"), value: "last_week" },
			{ label: t("filters.period.last_month"), value: "last_month" },
			{ label: t("filters.period.last_three_months"), value: "last_three_months" },
			{ label: t("filters.period.custom.value"), value: "custom" }
		];

		const customPeriodSelectors = this._getCustomPeriodSelectors();
		const displayingCounterContent = this._getDisplayingCounterContent();

		return (
			<div id="focusFeedFormPeriodContainer">
				{customPeriodSelectors}
				<SelectField
					id="focusFeedFormPeriodSelect"
					label={t("filters.period.value")}
					placeholder={t("filters.period.value")}
					menuItems={periodSelectItems}
					defaultValue={searchPeriod}
					value={searchPeriod}
					position="tr"
					onChange={value => {
						if (searchPeriod === "custom" && value !== "custom") this.setState({ dateRangeAnimating: true });
						onSetPeriod(value as SearchPeriod);
					}}
				/>
				{displayingCounterContent}
			</div>
		);
	}

	private _getPrivateDataLabel = () => {
		const { t, haveMentions, feedType } = this.props;
		if (feedType !== 'socialmedia' || !haveMentions) return '';
		return (
			<div>
				<span className='private-data-warning'>{t('definition.feed.form.info.no_private_data')}</span>
			</div>
		);
	}

	private _getRecoveryContent = () => {
		const { t, recoveryId, recoveryProgress, feedType } = this.props;
		const { onRecoverFeed } = this.props;

		if (recoveryId) return (
			<div id="focusFeedFormRecoveryProgress">
				<span>{t('feed.form.recovery.progress')}</span>
				<LinearProgress id="focusFeedFormRecoveryProgressLine" value={recoveryProgress} />
				<span>{`${recoveryProgress}%`}</span>
			</div>
		);
		return <Button id="focusFeedFormRecoverBtn" disabled={feedType === 'print'} flat onClick={() => onRecoverFeed()}>{t('feed.form.recovery.button')}</Button>;
	}

	public render() {
		const { t, feedType, brand_sync } = this.props;

		if (!feedType) return null;

		return (
			<div id="focusFeedForm" className={`${brand_sync && 'disabled'}`}>
				<div>
					<div><span className="focus-feed-results-preview-text">{t('feed.labels.results')}</span></div>
					{this._getRecoveryContent()}
				</div>
				{this._getPrivateDataLabel()}
				<div className="flex-end">
					{this._getSortSelectField()}
					{this._getPeriodSelectorContent()}
				</div>
			</div>
		);
	}
}

export default withT(FocusFeedForm);
